/**
 * Coups de pouce, Attendus 1213 « additionner des fractions », 1214
 * « soustraire des fractions », 1215 « multiplier des fractions », 1216
 * « diviser des fractions » (3e, Nombres, un fichier gen_1213 à quatre jeux
 * de clés). Seconde passe 3e (2026-09-13).
 *
 * ± (1213, 1214) : les quatre pages « un cas par page » de 1097 (4e), le
 * même geste (« d'abord le même dénominateur »), les mêmes sacrifiés,
 * importés tels quels : rien de nouveau à apprendre en 3e, seule la
 * fluidité.
 *
 * × (1215) : UNE page « Haut avec haut, bas avec bas », le produit tel
 * qu'il a été appris en 4e (programme l. 664, « calculer le produit de
 * nombres rationnels ») : 2/3 × 4/5, les numérateurs reliés en orange, les
 * dénominateurs en bleu, le résultat 8/15 ; la ligne du geste contre
 * l'erreur principale (produits en croix, DEPP 2de 2025 q.3).
 *
 * ÷ (1216) : UNE page « Diviser, c'est multiplier par l'inverse »
 * (programme l. 665-667 : l'inverse d'une fraction, diviser des fractions) :
 * 3/4 ÷ 2/5, la seconde fraction se retourne (flèche), 3/4 × 5/2 = 15/8.
 *
 * SACRIFIÉS (gen_1213, auto-testés par gen1213.self.test.js) : (2/3 ; 4/5)
 * en ×, (3/4 ; 2/5) en ÷ et (3/4 ; 5/2) en ×, dans les deux ordres.
 */
import cp1097 from './1097';
import { svg, frac, op } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';

/** Un texte centré (corps 12 par défaut). */
const ligne = (y, contenu, taille = 12, couleur = ENCRE, gras = false) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}"${gras ? ' font-weight="bold"' : ''}>${contenu}</text>`;
/** Une accolade fine reliant deux abscisses au-dessus (dessus = true) ou au-dessous d'une ligne. */
const lien = (x1, x2, y, couleur, dessus) => {
    const c = dessus ? y - 16 : y + 16;
    return `<path d="M ${x1} ${y} Q ${(x1 + x2) / 2} ${c} ${x2} ${y}" fill="none" stroke="${couleur}" stroke-width="1.6"/>`;
};

// ─── × : 2/3 × 4/5 = 8/15 ──────────────────────────────────────────────────

const SVG_PRODUIT = svg(
    '2 sur 3 fois 4 sur 5. En haut, 2 fois 4 égale 8, en orange. En bas, 3 fois 5 égale 15, en bleu. Le résultat : 8 sur 15. Tout droit : le haut avec le haut, le bas avec le bas, je ne croise pas.',
    `<!-- 1. le calcul, numérateurs orange, dénominateurs bleus -->
  ${frac(90, 70, '<tspan fill="' + ORANGE + '">2</tspan>', '<tspan fill="' + BLEU + '">3</tspan>', ENCRE, 18, 13)}
  ${op(122, 76, '×')}
  ${frac(154, 70, '<tspan fill="' + ORANGE + '">4</tspan>', '<tspan fill="' + BLEU + '">5</tspan>', ENCRE, 18, 13)}
  ${op(186, 76, '=')}
  ${frac(236, 70, '<tspan fill="' + ORANGE + '">2 × 4</tspan>', '<tspan fill="' + BLEU + '">3 × 5</tspan>', ENCRE, 16, 26)}
  ${op(290, 76, '=')}
  ${frac(322, 70, '<tspan fill="' + ORANGE + '">8</tspan>', '<tspan fill="' + BLEU + '">15</tspan>', ENCRE, 18, 13)}
  ${lien(96, 148, 44, ORANGE, true)}
  ${lien(96, 148, 98, BLEU, false)}

  <!-- 2. le geste -->
  ${ligne(150, '<tspan fill="' + ORANGE + '" font-weight="bold">En haut</tspan> : 2 × 4 = 8.   <tspan fill="' + BLEU + '" font-weight="bold">En bas</tspan> : 3 × 5 = 15.', 13)}
  ${ligne(176, 'Tout droit : le haut avec le haut, le bas avec le bas.', 12)}
  ${ligne(198, 'Je ne croise pas, je n’ajoute rien.', 12, GRIS)}`,
    216,
);

// ─── ÷ : 3/4 ÷ 2/5 = 3/4 × 5/2 = 15/8 ─────────────────────────────────────

const SVG_QUOTIENT = svg(
    '3 sur 4 divisé par 2 sur 5. La fraction 2 sur 5 se retourne : son inverse est 5 sur 2. 3 sur 4 divisé par 2 sur 5 égale 3 sur 4 fois 5 sur 2 égale 15 sur 8. Seule la fraction après le signe divisé se retourne.',
    `<!-- 1. la division et sa transformation -->
  ${frac(60, 70, 3, 4, ENCRE, 18, 13)}
  ${op(92, 76, '÷')}
  ${frac(124, 70, 2, 5, ORANGE, 18, 13)}
  ${op(160, 76, '=')}
  ${frac(196, 70, 3, 4, ENCRE, 18, 13)}
  ${op(228, 76, '×')}
  ${frac(260, 70, 5, 2, ORANGE, 18, 13)}
  ${op(296, 76, '=')}
  ${frac(332, 70, 15, 8, ENCRE, 18, 13)}
  <!-- la flèche « je retourne » de 2/5 vers 5/2 -->
  <path d="M 138 104 Q 192 134 246 104" fill="none" stroke="${ORANGE}" stroke-width="1.6" marker-end="url(#fl)"/>
  <defs><marker id="fl" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M 0 0 L 8 4 L 0 8 z" fill="${ORANGE}"/></marker></defs>
  <text x="192" y="132" font-size="11" text-anchor="middle" fill="${ORANGE}">je retourne</text>

  <!-- 2. le geste -->
  ${ligne(166, 'L’inverse de <tspan fill="' + ORANGE + '" font-weight="bold">2/5</tspan>, c’est <tspan fill="' + ORANGE + '" font-weight="bold">5/2</tspan>.', 13)}
  ${ligne(190, 'Diviser par une fraction, c’est multiplier par son inverse.', 12)}
  ${ligne(212, 'Seule la fraction après le ÷ se retourne.', 12, GRIS)}`,
    230,
);

const AIDE_PLUS_MOINS = cp1097.flash_texte_trou_saisie;
const AIDE_PRODUIT = { pages: [{ titre: 'Haut avec haut, bas avec bas', svg: SVG_PRODUIT }] };
const AIDE_QUOTIENT = { pages: [{ titre: 'Diviser, c’est multiplier par l’inverse', svg: SVG_QUOTIENT }] };

/** Les quatre attendus, chaque clé vers l'aide de son opération. */
export const CP_1213 = {
    somme_flash_texte_trou_saisie: AIDE_PLUS_MOINS,
    somme_resultat_qcm_unique: AIDE_PLUS_MOINS,
    somme_sens_tableau_vf: AIDE_PLUS_MOINS,
};
export const CP_1214 = {
    difference_flash_texte_trou_saisie: AIDE_PLUS_MOINS,
    difference_resultat_qcm_unique: AIDE_PLUS_MOINS,
    difference_sens_tableau_vf: AIDE_PLUS_MOINS,
};
export const CP_1215 = {
    produit_flash_texte_trou_saisie: AIDE_PRODUIT,
    produit_resultat_qcm_unique: AIDE_PRODUIT,
    produit_sens_tableau_vf: AIDE_PRODUIT,
};
export const CP_1216 = {
    quotient_flash_texte_trou_saisie: AIDE_QUOTIENT,
    quotient_resultat_qcm_unique: AIDE_QUOTIENT,
    quotient_sens_tableau_vf: AIDE_QUOTIENT,
};

export default CP_1213;
